import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import GlobalTable from '../components/GlobalTable';
import { useLazyTable } from '../hooks/useLazyTable';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import useApiCall from '../hooks/useApiCall';

const RefillReminders = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
  const [loading, setLoading] = useState(false);
  const { push, beginDrilldown } = useBreadcrumbs();
  const navigate = useNavigate();

  const loadData = () => {
    setLoading(true);
    axiosInstance.get('/api/refill-reminders/', { params: queryParams })
      .then(res => {
        setData(res.data.results || []);
        setTotalRecords(res.data.count || 0);
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [queryParams]);

  // Per-row loading tracked by row id
  const [sendingSms, setSendingSms] = useState({});

  const handleSendSms = async (row) => {
    if (sendingSms[row.id]) return;
    setSendingSms(prev => ({ ...prev, [row.id]: true }));
    try {
      await axiosInstance.post(`/api/refill-reminders/${row.id}/send-sms/`);
      handleApiSuccess(`SMS reminder sent to ${row.customer_name}.`);
      setData(prev =>
        prev.map(r =>
          r.id === row.id ? { ...r, sms_sent_at: new Date().toISOString() } : r
        )
      );
    } catch (err) {
      handleApiError(err);
    } finally {
      setSendingSms(prev => ({ ...prev, [row.id]: false }));
    }
  };

  const statusTemplate = row => (
    <Tag
      value={row.is_completed ? 'Completed' : 'Pending'}
      severity={row.is_completed ? 'success' : 'warning'}
    />
  );

  const smsTemplate = row => {
    const alreadySent = !!row.sms_sent_at;
    const isLoading = !!sendingSms[row.id];
    const sentLabel = alreadySent
      ? `Sent ${new Date(row.sms_sent_at).toLocaleDateString()}`
      : 'Send SMS';

    return (
      <Button
        icon={alreadySent ? 'pi pi-check' : 'pi pi-send'}
        label={sentLabel}
        className={`p-button-sm ${alreadySent ? 'p-button-success p-button-outlined' : 'p-button-outlined'}`}
        disabled={alreadySent || row.is_completed || isLoading}
        loading={isLoading}
        onClick={() => handleSendSms(row)}
        tooltip={
          alreadySent
            ? `SMS sent at ${new Date(row.sms_sent_at).toLocaleString()}`
            : 'Send SMS refill reminder to this customer'
        }
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  const columns = [
    {
      Header: 'Customer',
      accessor: 'customer_name',
      filtered: true,
      bodyTemplate: (row) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer' }}
          onClick={() => {
            beginDrilldown();
            navigate(`/customers/${row.customer}`);
            push({ label: `Refill Reminders`, path: '/refill-reminders' });
            push({ label: `Customer: ${row.customer_name}`, path: `/customers/${row.customer}` });
          }}
        >
          {row.customer_name}
        </span>
      )
    },
    {
      Header: 'Prescription Id',
      accessor: 'prescription',
      filtered: true,
      bodyTemplate: (row) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer' }}
          onClick={() => {
            beginDrilldown();
            navigate('/prescriptions', { state: { prescriptionId: row.prescription } });
            push({ label: `Refill Reminders`, path: '/refill-reminders' });
            push({ label: `Prescriptions`, path: '/prescriptions' });
            push({
              label: `Prescription: ${row.prescription}`,
              path: '/prescriptions',
              state: { prescriptionId: row.prescription }
            });
          }}
        >
          {row.prescription}
        </span>
      )
    },
    { Header: 'Refill Date',    accessor: 'refill_date',   dataType: 'date' },
    { Header: 'Reminder Date',  accessor: 'reminder_date', dataType: 'date' },
    { Header: 'Status',         bodyTemplate: statusTemplate },
    { Header: 'SMS Reminder',   bodyTemplate: smsTemplate },
  ];

  return (
    <div className="containers">
      <GlobalTable
        columns={columns}
        data={data}
        loading={loading}
        tabletitle="Refill Reminders"
        showEditColumn={false}
        lazy
        totalRecords={totalRecords}
        lazyParams={lazyParams}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        headerAction={
          <Button icon="pi pi-refresh" label="Refresh" outlined onClick={loadData} />
        }
      />
    </div>
  );
};

export default RefillReminders;